/**
 * Google Analytics 4 통합
 * 
 * 사용자 퍼널 추적을 위한 이벤트:
 * - page_view: 페이지 방문
 * - form_start: 폼 작성 시작
 * - file_upload: 테스트케이스 파일 업로드
 * - evaluate_submit: 평가 요청
 * - evaluate_success: 평가 성공
 * - evaluate_error: 평가 실패
 * - result_view: 결과 확인
 * - pdf_download: PDF 다운로드
 * - reset_click: 다시 평가하기
 */

// GA4 측정 ID (환경 변수에서 가져옴)
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || '';

// Production 환경에서만 GA4 활성화
const IS_PRODUCTION = import.meta.env.MODE === 'production';
const GA_ENABLED = IS_PRODUCTION && !!GA_MEASUREMENT_ID;

// gtag 타입 정의
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      params?: Record<string, unknown>
    ) => void;
    dataLayer: unknown[];
  }
}

/**
 * GA4 초기화
 */
export function initGA(): void {
  if (!GA_ENABLED) {
    if (!IS_PRODUCTION) {
      console.log('[Analytics] GA4 disabled in development mode');
    } else if (!GA_MEASUREMENT_ID) {
      console.warn('[Analytics] GA_MEASUREMENT_ID not configured');
    }
    return;
  }

  // gtag 스크립트 동적 로드
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // dataLayer 초기화 (Google 공식 방식)
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, prefer-rest-params
  window.gtag = function() {
    // Google의 공식 gtag 패턴: arguments 객체를 그대로 push
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  } as typeof window.gtag;

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // 수동으로 페이지뷰 전송
  });
}

/**
 * 페이지뷰 전송
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (!GA_ENABLED || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
}

/**
 * 이벤트 전송
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>
): void {
  if (!GA_ENABLED || !window.gtag) return;

  window.gtag('event', eventName, params);
}

// 퍼널 이벤트 헬퍼 함수들
export const analytics = {
  // 폼 작성 시작
  formStart: () => trackEvent('form_start'),

  // 파일 업로드
  fileUpload: (fileType: string, rowCount: number) =>
    trackEvent('file_upload', { file_type: fileType, row_count: rowCount }),

  // 평가 요청
  evaluateSubmit: (hasTestCases: boolean, hasPlanning: boolean) =>
    trackEvent('evaluate_submit', {
      has_test_cases: hasTestCases,
      has_planning: hasPlanning,
    }),

  // 평가 성공
  evaluateSuccess: (status: string, score: number) =>
    trackEvent('evaluate_success', { status, score }),

  // 평가 실패
  evaluateError: (errorMessage: string) =>
    trackEvent('evaluate_error', { error_message: errorMessage }),

  // 결과 확인
  resultView: (status: string) => trackEvent('result_view', { status }),

  // PDF 다운로드
  pdfDownload: (status: string) => trackEvent('pdf_download', { status }),

  // 다시 평가하기
  resetClick: () => trackEvent('reset_click'),

  // 언어 변경
  languageChange: (language: string) =>
    trackEvent('language_change', { language }),

  // 테마 변경
  themeChange: (theme: string) =>
    trackEvent('theme_change', { theme }),

  // 별점 평가 제출
  ratingSubmit: (rating: number, hasFeedback: boolean) =>
    trackEvent('rating_submit', { rating, has_feedback: hasFeedback }),

  // 별점 평가 스킵
  ratingSkip: (type: 'later' | 'never') =>
    trackEvent('rating_skip', { skip_type: type }),

  // === 네비게이션 이벤트 ===
  
  // 히스토리 버튼 클릭
  historyClick: () => trackEvent('history_click'),

  // 가격 보기 클릭
  pricingClick: () => trackEvent('pricing_click'),

  // 대시보드 접근
  dashboardClick: () => trackEvent('dashboard_click'),

  // === 인증 이벤트 ===
  
  // 로그인 버튼 클릭
  loginClick: () => trackEvent('login_click'),

  // 로그인 성공
  loginSuccess: () => trackEvent('login_success'),

  // 회원가입 완료
  signupComplete: () => trackEvent('signup_complete'),

  // 로그아웃
  logoutClick: () => trackEvent('logout_click'),

  // === 사용자 메뉴 이벤트 ===
  
  // 프로필 관리 클릭
  profileClick: () => trackEvent('profile_click'),

  // 설정 클릭
  settingsClick: () => trackEvent('settings_click'),

  // 팀 관리 클릭
  teamManageClick: () => trackEvent('team_manage_click'),

  // API 키 관리 클릭
  apiKeysClick: () => trackEvent('api_keys_click'),

  // 구독 관리 클릭
  subscriptionManageClick: () => trackEvent('subscription_manage_click'),

  // === 쿠폰 이벤트 ===
  couponClick: () => trackEvent('coupon_click'),
  couponRedeemAttempt: () => trackEvent('coupon_redeem_attempt'),
  couponRedeemSuccess: () => trackEvent('coupon_redeem_success'),
  couponRedeemFail: () => trackEvent('coupon_redeem_fail'),
  couponRegisterAttempt: () => trackEvent('coupon_register_attempt'),
  couponRegisterSuccess: () => trackEvent('coupon_register_success'),
  couponRegisterFail: () => trackEvent('coupon_register_fail'),

  // === 가격/플랜 이벤트 ===
  
  // 플랜 선택
  planSelect: (plan: string, billingPeriod: string) =>
    trackEvent('plan_select', { plan, billing_period: billingPeriod }),

  // 결제 주기 변경
  billingPeriodChange: (period: string) =>
    trackEvent('billing_period_change', { period }),

  // === 온보딩 이벤트 ===
  
  // 온보딩 완료
  onboardingComplete: () => trackEvent('onboarding_complete'),

  // 온보딩 스킵
  onboardingSkip: (step: number) =>
    trackEvent('onboarding_skip', { step }),

  // === 기타 이벤트 ===
  
  // MAX AI 리포트 열기
  maxReportView: () => trackEvent('max_report_view'),

  // 히스토리 상세 보기
  historyDetailView: (recordId: number) =>
    trackEvent('history_detail_view', { record_id: recordId }),

  // 데모 회원가입 팝업에서 로그인 클릭
  demoSignupClick: () => trackEvent('demo_signup_click'),

  // === Welcome 온보딩 이벤트 ===
  
  // Welcome 온보딩에서 사용자 유형 선택
  welcomeUserTypeSelect: (userType: string) =>
    trackEvent('welcome_user_type_select', { user_type: userType }),

  // Welcome 온보딩에서 플랜 선택
  welcomePlanSelect: (plan: string, billingPeriod: string) =>
    trackEvent('welcome_plan_select', { plan, billing_period: billingPeriod }),

  // Welcome 온보딩에서 구경만 클릭
  welcomeJustBrowse: () => trackEvent('welcome_just_browse'),
};

